import * as React from 'react';

import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { Autocomplete, Button, Grid, Link, PasswordField, Spacer, TextField, Typography } from '@emburse/ui-core';

import { getPartnerAppLogoPath, getPartnerSignupLogoStyle } from '@ReactComponents/Branding/partnerBranding';

import { CloseModal, ShowModal } from '@Actions/index';

import { InternationalizationApi } from '@ClientApi/internationalization';
import { Api } from '@ClientServices/api';
import { IServerCountryInfo } from '@Shared/constants/international_constants';
import { PARTNER_ID_WTI_GLOBAL } from '@Shared/constants/partners';

import { SignUpDupeModal } from './SignUpDupeModal/SignUpDupeModal';
import SignUpReviews from './SignUpReviews/SignUpReviews';
import { SignUpTerms } from './SignUpTerms/SignUpTerms';

import * as style from './SignUpPage.styl';

interface SignUpPageProps {}

export const SignUpPage: React.FC<SignUpPageProps> = () => {
  const dispatch = useDispatch();
  const internationalMode = window.FEATURES?.international_signup;
  const [searchParams] = useSearchParams();
  const channel = searchParams.get('channel');
  const [state, setState] = React.useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    business_name: '',
    tos_accepted: false,
    partner_id: channel || '',
    address_country_code: ''
  });

  const [isSubmitting, setISubmitting] = React.useState(false);
  const defaultErrorState = {
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    business_name: '',
    tos_accepted: '',
    address_country_code: ''
  };

  const [errorState, setErrorState] = React.useState(defaultErrorState);

  const [internationalCountryList, setInternationalCountryList] = React.useState<IServerCountryInfo[]>([]);
  const [defaultCountry, setDefaultCountry] = React.useState<IServerCountryInfo | null>(null);

  React.useEffect(() => {
    async function fetchCountries() {
      const { countryList, defaultCountry: fetchedDefaultCountry } = await InternationalizationApi.getServerCountryOptions();

      setInternationalCountryList(countryList);
      setDefaultCountry(fetchedDefaultCountry);
      if (state.address_country_code === '' && fetchedDefaultCountry) {
        updateStateAndClearError('address_country_code', fetchedDefaultCountry.countryCode);
      }
    }
    if (internationalMode) {
      fetchCountries();
    }
  }, []);

  function validateFields() {
    const fields = ['first_name', 'last_name', 'password', 'email', 'business_name', 'tos_accepted'];
    const invalid: Record<string, string> = {};
    fields.forEach((field) => {
      if (!(state as any)[field]) {
        invalid[field] = '* Mandatory field';
      }
    });
    if (Object.keys(invalid).length > 0) {
      throw new Error(
        JSON.stringify({
          error: 'Please fill in all fields',
          validation_errors: invalid
        })
      );
    }
  }

  async function createUser(bypassDupeCheck = false) {
    try {
      validateFields();
      if (isSubmitting) {
        toast.info('Already submitted! Please wait.');
      } else {
        setISubmitting(true);
      }
      const resp = await Api.constructRequest('POST', '/signup', {
        ...state,
        _csrf: (window as any).csrf,
        newSignup: true,
        bypassDupeCheck
      });
      if (resp.redirected) {
        // we are rate limited
        throw new Error(
          JSON.stringify({
            error: 'Error Processing Request, please try again later.'
          })
        );
      }
      window.location.href = '/signup/onboarding';
    } catch (e: any) {
      let errorMsg;
      try {
        const parsed = JSON.parse(e.message);
        errorMsg = parsed?.error;
        setErrorState({
          ...defaultErrorState,
          ...(parsed?.validation_errors || {})
        });
      } catch (jsonErr) {
        errorMsg = e.message;
      }
      if (errorMsg === 'dupe_company') {
        showDupeModal();
      } else if (errorMsg) {
        dispatch(new CloseModal());
        toast.error(errorMsg);
      }
    } finally {
      setISubmitting(false);
    }
  }

  const logo = getPartnerAppLogoPath({
    isWtiBranding: channel === PARTNER_ID_WTI_GLOBAL,
    defaultLogoPath: window.whitelabelConfiguration?.tags?.appLogoPath
  });
  const logoStyle = getPartnerSignupLogoStyle({ isWtiBranding: channel === PARTNER_ID_WTI_GLOBAL });
  const whiteLabelName = window.whitelabelConfiguration?.tags?.brandName;

  function updateStateAndClearError(field: string, value: string | boolean) {
    setState({
      ...state,
      [field]: value
    });
    setErrorState({
      ...errorState,
      [field]: ''
    });
  }

  function showDupeModal() {
    return dispatch(
      new ShowModal(SignUpDupeModal, {
        onConfirm: () => createUser(true),
        isLoading: isSubmitting
      })
    );
  }

  return (
    <div className={style.global} style={{ height: '100%' }}>
      <Grid className="page" container alignItems="flex-start" wrap="nowrap" spacing={0}>
        <Grid item container className="reviewsLeft" alignContent="center">
          <SignUpReviews initialSignupPage />
        </Grid>
        <Grid item container className="signUpRight" alignContent="center" justifyContent="center">
          <Grid container alignItems="center" wrap="nowrap" spacing={3} direction="column" className="form">
            <Grid item className="maxWidth centerText">
              <img className="logo" alt="logo" src={logo} style={logoStyle} />
              <Spacer size={'12px'} />
            </Grid>

            <Grid item className="maxWidth">
              <Grid container alignItems="flex-start" wrap="nowrap" spacing={2}>
                <Grid item xl className="halfWidth">
                  <TextField
                    label="First Name "
                    required
                    fullWidth
                    value={state.first_name}
                    onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => updateStateAndClearError('first_name', event.target.value)}
                    error={Boolean(errorState.first_name)}
                    helperText={errorState.first_name}
                  />
                </Grid>
                <Grid item xl className="halfWidth">
                  <TextField
                    label="Last Name"
                    required
                    fullWidth
                    value={state.last_name}
                    onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => updateStateAndClearError('last_name', event.target.value)}
                    error={Boolean(errorState.last_name)}
                    helperText={errorState.last_name}
                  />
                </Grid>
              </Grid>
            </Grid>

            <Grid item className="maxWidth">
              <TextField
                label="Legally Registered Business Name"
                required
                fullWidth
                value={state.business_name}
                onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => updateStateAndClearError('business_name', event.target.value)}
                error={Boolean(errorState.business_name)}
                helperText={errorState.business_name}
              />
            </Grid>

            <Grid item className="maxWidth">
              <TextField
                label="Work Email Address"
                required
                fullWidth
                value={state.email}
                onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => updateStateAndClearError('email', event.target.value)}
                error={Boolean(errorState.email)}
                helperText={errorState.email}
              />
            </Grid>

            <Grid item className="maxWidth">
              <PasswordField
                id="password"
                label="Create Password"
                required
                fullWidth
                value={state.password}
                onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => updateStateAndClearError('password', event.target.value)}
                error={Boolean(errorState.password)}
                helperText={errorState.password}
              />
            </Grid>

            {!internationalMode && (
              <Grid item>
                <Typography variant="body2" className="centerText">
                  {whiteLabelName} is designed exclusively for businesses based in the United States.
                </Typography>
              </Grid>
            )}
            {internationalMode && (
              <Grid item className="maxWidth">
                <Autocomplete
                  label="Company Country"
                  required
                  fullWidth
                  onChange={(_: any, val: IServerCountryInfo) => updateStateAndClearError('address_country_code', val.countryCode)}
                  error={Boolean(errorState.address_country_code)}
                  helperText={errorState.address_country_code}
                  value={
                    internationalCountryList.find((countryInfo) => countryInfo.countryCode === state.address_country_code) || defaultCountry
                  }
                  options={internationalCountryList}
                  getOptionLabel={(option: IServerCountryInfo) => option.countryName}
                  disabled={internationalCountryList.length <= 1}
                />
              </Grid>
            )}

            <Grid item className="maxWidth">
              <SignUpTerms
                checked={state.tos_accepted}
                error={Boolean(errorState.tos_accepted)}
                onChange={(newCheckedState: boolean) => updateStateAndClearError('tos_accepted', newCheckedState)}
                whitelabelName={whiteLabelName}
                partner_id={state.partner_id}
              />
            </Grid>

            <Grid item>
              <Button onClick={() => createUser()} disabled={isSubmitting}>
                Get Started
              </Button>
            </Grid>
            <Grid item>
              <Spacer size={'12px'} />
              <Typography>
                Already have an account?{' '}
                <Link href="/login" underline="none">
                  Login
                </Link>
              </Typography>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </div>
  );
};
